import React, { useEffect, useState } from 'react';
// import './ScoreContainer.scss';
import { useTranslation } from 'react-i18next';
import { CommandPrefixes } from '../../commands/practitioner/enums/CommandPrefixes';
import BackendService from '../../services/BackendService';
import { ShowMessageCommandTypes } from '../../commands/practitioner/enums/ShowMessageCommandTypes';
import { ScoreContainerStyles } from './styles';
import { Typography, Box, Tooltip } from '@mui/material';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';

interface ContainerProps {
  name: string;
  scores?: any[];
  type: string;
  sessionId: string;
  treatmentId: string;
  setValues: Function;
  possibleScores: number[];
  showVisualAidCommand: () => void;
}

const ScoreContainer: React.FC<ContainerProps> = ({
  name,
  scores = [],
  setValues,
  type,
  sessionId,
  treatmentId,
  possibleScores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  showVisualAidCommand,
}) => {
  const { t } = useTranslation();
  const isSUD = type === CommandPrefixes.SUD;
  const {
    taskHasStarted,
    setShowSUDAidPressed,
    setShowVOCAidPressed,
    showSUDAidPressed,
    showVOCAidPressed,
  } = usePractitionerControlsContext();
  const [isAdding, setIsAdding] = useState(false);
  const [selectedValue, setSelectedValue] = useState(-1);
  const values = possibleScores;
  const ScoreName = name === t('sud');
  const [hoveredScore, setHoveredScore] = useState(-1);
  const saveDisabled = selectedValue === -1;
  const { scoreContainer } = ScoreContainerStyles;
  const SUDColors = [
    '#90FF75',
    '#9FFF75',
    '#C2FF75',
    '#E1FF75',
    '#FFF375',
    '#FFDE75',
    '#FCBF7A',
    '#F1B287',
    '#E9AA93',
    '#E2A49E',
    '#E0A1A0',
  ];
  const VOCColors = ['#9EDCE2', '#E575FF', '#CD75FF', '#9575FF', '#7583FF', '#75ACFF', '#94D3E9'];

  const colors = isSUD ? SUDColors : VOCColors;

  useEffect(() => {
    if (taskHasStarted) {
      setShowSUDAidPressed(false);
      setShowVOCAidPressed(false);
    }
  }, [taskHasStarted]);

  const cleanUpAddingState = () => {
    setSelectedValue(-1);
    setIsAdding(false);
    setHoveredScore(-1);
  };

  const cancelShowSUDAidPressed = () => {
    cleanUpAddingState();
    if (showSUDAidPressed) {
      setShowSUDAidPressed(false);
    }
    BackendService.sendShowMessageCommand(sessionId, treatmentId, ShowMessageCommandTypes.Hide);
  };

  const cancelShowVOCAidPressed = () => {
    cleanUpAddingState();
    if (showVOCAidPressed) {
      setShowVOCAidPressed(false);
    }
    BackendService.sendShowMessageCommand(sessionId, treatmentId, ShowMessageCommandTypes.Hide);
  };

  const handleShowVisualAidCommandSUD = () => {
    if (showVOCAidPressed) setShowVOCAidPressed(false);
    if (showSUDAidPressed) {
      setShowSUDAidPressed(false);
      BackendService.sendShowMessageCommand(sessionId, treatmentId, ShowMessageCommandTypes.Hide);
    } else {
      setShowSUDAidPressed(true);
      showVisualAidCommand();
    }
  };

  const handleShowVisualAidCommandVOC = () => {
    if (showSUDAidPressed) setShowSUDAidPressed(false);
    if (showVOCAidPressed) {
      setShowVOCAidPressed(false);
      BackendService.sendShowMessageCommand(sessionId, treatmentId, ShowMessageCommandTypes.Hide);
    } else {
      setShowVOCAidPressed(true);
      showVisualAidCommand();
    }
  };

  const save = () => {
    const selectedColor = colors[selectedValue % colors.length];
    if (isSUD) {
      BackendService.sendSUDCommand(sessionId, treatmentId, selectedValue.toString());
    } else {
      BackendService.sendVOCCommand(sessionId, treatmentId, selectedValue.toString());
    }
    setValues([
      ...scores,
      {
        value: selectedValue,
        color: selectedColor,
        key: Math.random()
          .toString(36)
          .slice(2),
      },
    ]);
    cleanUpAddingState();
  };

  const deleteScore = (key: any) => {
    const newScores = scores.filter((scoreToFilter: any) => key !== scoreToFilter?.key);
    setValues(newScores);
    cleanUpAddingState();
  };

  if (isAdding) {
    return (
      <Box sx={scoreContainer} className={'score-container adding'}>
        <Box className={'score'}>
          <Box className={'score-title'}>
            <Typography className="text">{name}</Typography>
          </Box>
          {values.map((score, index) => {
            return (
              <Box
                className={`score-count ${score === selectedValue ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedValue(score);
                }}
                key={name + 'add-score' + score}
                style={{ backgroundColor: colors[score % colors.length] }}
              >
                <Typography variant="caption" className="score-value">
                  {score}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Box className={'controls'}>
          <button
            style={{ opacity: saveDisabled ? 0.5 : 1, cursor: saveDisabled ? 'not-allowed' : 'pointer' }}
            className="save"
            onClick={save}
            disabled={saveDisabled}
          >
            {t('save')}
          </button>
          <button className={'cancel'} onClick={ScoreName ? cancelShowSUDAidPressed : cancelShowVOCAidPressed}>
            {t('cancel')}
          </button>
          <Tooltip placement="bottom-end" title={t('score-info')}>
            <button
              className={taskHasStarted ? 'info-disabled' : 'show-info'}
              disabled={taskHasStarted}
              onClick={ScoreName ? handleShowVisualAidCommandSUD : handleShowVisualAidCommandVOC}
            >
              {t((ScoreName ? showSUDAidPressed : showVOCAidPressed) ? 'hide-visual-aid' : 'show-visual-aid')}
            </button>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={scoreContainer} className={'score-container'}>
      <Box className={'score-title'}>
        <Typography className="text">{name}</Typography>
      </Box>
      <Box className={'score'}>
        {scores.map((score: any) => {
          return (
            <Box
              className={`score-count`}
              onMouseEnter={() => setHoveredScore(score.key)}
              onMouseLeave={() => setHoveredScore(-1)}
              key={name + 'score' + score.key + score.value}
              style={{ backgroundColor: score.color }}
            >
              <Typography variant="caption" className="score-value">
                {score.value}
              </Typography>
            </Box>
          );
        })}
        <Box className={'score-count add'} onClick={() => setIsAdding(!isAdding)}>
          +
        </Box>
      </Box>
    </Box>
  );
};

export default ScoreContainer;
